import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Bug, MessageSquare, X, Image as ImageIcon, Loader2 } from 'lucide-react';
import client from '../../api/client';
import { APP_VERSION } from '../../pages/AppInfoPage';

const MAX_SCREENSHOTS = 3;
const MAX_MESSAGE = 2000;

// Upload a File directly to Cloudinary using the signed-upload flow.
// Returns { public_id, secure_url }. Uses XHR so the project's axios
// interceptors (which inject our JWT) never leak into the Cloudinary request.
function uploadFileToCloudinary(file, sig) {
  return new Promise((resolve, reject) => {
    const form = new FormData();
    form.append('file', file);
    form.append('api_key', sig.api_key);
    form.append('timestamp', sig.timestamp);
    form.append('signature', sig.signature);
    form.append('folder', sig.folder);
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`);
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const data = JSON.parse(xhr.responseText);
          resolve({ public_id: data.public_id, secure_url: data.secure_url });
        } catch (e) {
          reject(new Error('Cloudinary returned non-JSON'));
        }
      } else {
        reject(new Error(`Cloudinary upload failed: ${xhr.status} ${xhr.responseText.slice(0, 200)}`));
      }
    };
    xhr.onerror = () => reject(new Error('Cloudinary network error'));
    xhr.send(form);
  });
}

export default function FeedbackDialog({ open, onOpenChange }) {
  const { t } = useTranslation();
  const [type, setType] = useState('bug');
  const [message, setMessage] = useState('');
  const [screenshots, setScreenshots] = useState([]); // [{ public_id, url }]
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  const reset = () => {
    setType('bug');
    setMessage('');
    setScreenshots([]);
    setUploading(false);
    setSubmitting(false);
  };

  const handleClose = (next) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const handleAddScreenshot = () => {
    if (screenshots.length >= MAX_SCREENSHOTS || uploading) return;
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const sigRes = await client.get('/uploads/signature', { params: { context: 'feedback' } });
      const shot = await uploadFileToCloudinary(file, sigRes.data);
      setScreenshots(prev => [...prev, { public_id: shot.public_id, url: shot.secure_url }]);
    } catch (err) {
      console.error('feedback upload failed:', err);
      toast.error(t('feedback.uploadFailed'));
    } finally {
      setUploading(false);
    }
  };

  const removeScreenshot = (idx) => {
    setScreenshots(prev => prev.filter((_, i) => i !== idx));
  };

  const canSubmit = message.trim().length > 0 && !submitting && !uploading;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await client.post('/feedback', {
        type,
        message: message.trim(),
        screenshots,
        page_url: typeof window !== 'undefined' ? window.location.href : '',
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        app_version: APP_VERSION,
      });
      toast.success(t('feedback.submitted'));
      handleClose(false);
    } catch (err) {
      console.error(err);
      toast.error(t('feedback.submitFailed'));
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg max-h-[85dvh] overflow-y-auto" data-testid="feedback-dialog" compact>
        <DialogHeader>
          <DialogTitle>{t('feedback.title')}</DialogTitle>
          <DialogDescription>{t('feedback.subtitle')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Type toggle */}
          <div>
            <label className="text-sm font-medium mb-2 block">{t('feedback.typeLabel')}</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setType('bug')}
                className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition ${
                  type === 'bug' ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:bg-muted/50'
                }`}
                data-testid="feedback-type-bug"
              >
                <Bug className="h-4 w-4" />
                <span className="font-medium">{t('feedback.typeBug')}</span>
              </button>
              <button
                type="button"
                onClick={() => setType('feedback')}
                className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition ${
                  type === 'feedback' ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:bg-muted/50'
                }`}
                data-testid="feedback-type-feedback"
              >
                <MessageSquare className="h-4 w-4" />
                <span className="font-medium">{t('feedback.typeFeedback')}</span>
              </button>
            </div>
          </div>

          {/* Message */}
          <div>
            <label className="text-sm font-medium mb-2 block">{t('feedback.messageLabel')}</label>
            <div className="relative">
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value.slice(0, MAX_MESSAGE))}
                placeholder={t('feedback.messagePlaceholder')}
                rows={5}
                className="resize-none"
                data-testid="feedback-message"
              />
              <span className="absolute bottom-2 right-3 text-xs text-muted-foreground">
                {t('feedback.messageCounter', { count: message.length })}
              </span>
            </div>
          </div>

          {/* Screenshots */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium">{t('feedback.screenshotLimit')}</label>
              <span className="text-xs text-muted-foreground">{screenshots.length}/{MAX_SCREENSHOTS}</span>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {screenshots.map((s, i) => (
                <div key={s.public_id} className="relative aspect-square rounded-lg overflow-hidden border">
                  <img src={s.url} alt="" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removeScreenshot(i)}
                    className="absolute top-1 right-1 rounded-full bg-black/60 p-1 text-white hover:bg-black/80"
                    data-testid={`feedback-remove-screenshot-${i}`}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
              {screenshots.length < MAX_SCREENSHOTS && (
                <button
                  type="button"
                  onClick={handleAddScreenshot}
                  disabled={uploading}
                  className="aspect-square rounded-lg border-2 border-dashed border-border hover:border-primary hover:bg-primary/5 transition flex flex-col items-center justify-center gap-1 disabled:opacity-50"
                  data-testid="feedback-add-screenshot"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      <span className="text-xs">{t('feedback.uploading')}</span>
                    </>
                  ) : (
                    <>
                      <ImageIcon className="h-5 w-5" />
                      <span className="text-xs">{t('feedback.addScreenshot')}</span>
                    </>
                  )}
                </button>
              )}
            </div>
            <input
              type="file"
              ref={fileInputRef}
              accept="image/*"
              onChange={handleFileChange}
              className="hidden"
              data-testid="feedback-file-input"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)} disabled={submitting}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit} data-testid="feedback-submit-btn">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            {t('feedback.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
